import { Action, on } from '@ngrx/store';
import { produce } from 'immer';
import { cloneDeep, isEqual, isNil, isNumber } from 'lodash';
import { BackupHistory } from 'mbs-ui-shared@models/backupHistory';
import { isDataObsolete } from 'mbs-ui-shared@utils/store';
import moment from 'moment';
import { createImmerReducer } from 'ngrx-immer/store';
import * as BackupHistoryActions from './actions';
import { BackupHistoryState, backupHistoryAdapter, initialBackupHistoryState } from './state';

const reducer = createImmerReducer(
  initialBackupHistoryState,
  on(BackupHistoryActions.loadPage, (state, action) => {
    let clear = false;

    state.paging.currentPageNumber = action.pageNumber ?? state.paging.currentPageNumber;

    if (action.isFullRefresh || isDataObsolete(state.paging.obsolescenceMark)) {
      clear = true;
    }

    const filters = cloneDeep(action.filters);

    if (isNumber(filters.showLatestPlanResult)) {
      state.showLatestPlanResult = filters.showLatestPlanResult;
      delete filters.showLatestPlanResult;
    }

    if (!isNil(filters) && !isEqual(state.paging.filters, filters)) {
      state.paging.filters = filters;
      state.paging.currentPageNumber = initialBackupHistoryState.paging.currentPageNumber;
      clear = true;
    }

    if (action.perPage && state.paging.perPage !== action.perPage) {
      state.paging.perPage = action.perPage;
      state.paging.currentPageNumber = initialBackupHistoryState.paging.currentPageNumber;
      clear = true;
    }

    state = clear ? clearPaging(state) : state;

    state.paging.loading = !(state.paging.loaded && !!state.paging.pages[state.paging.currentPageNumber]);

    return state;
  }),
  on(BackupHistoryActions.loadPageSuccess, (state, action) => {
    return replaceEntities(
      action.entities,
      produce(state, (draft) => {
        draft.paging = {
          ...draft.paging,
          pages: {
            ...draft.paging.pages,
            [action.pageNumber]: [...action.entities.map((entity) => backupHistoryAdapter.selectId(entity) as string)]
          },
          total: action.total,
          loading: false,
          loaded: true,
          obsolescenceMark: draft.paging.obsolescenceMark ?? moment().valueOf()
        };
        draft.totalComputers = action.totalComputers;
      })
    );
  }),
  on(BackupHistoryActions.loadPageError, (state) => {
    state.paging.loading = false;

    return state;
  })
);

export function backupHistoryReducer(state: BackupHistoryState | undefined, action: Action) {
  return reducer(state, action);
}

function replaceEntities(entities: BackupHistory[], state: BackupHistoryState): BackupHistoryState {
  const newState = backupHistoryAdapter.removeMany(
    entities.map((entity) => backupHistoryAdapter.selectId(entity) as string),
    state
  );

  return backupHistoryAdapter.addMany(entities, newState);
}

function clearPaging(state: BackupHistoryState, clearAllFields = false): BackupHistoryState {
  state.paging = {
    ...state.paging,
    pages: {},
    total: initialBackupHistoryState.paging.total,
    loaded: initialBackupHistoryState.paging.loaded,
    obsolescenceMark: initialBackupHistoryState.paging.obsolescenceMark,
    loading: initialBackupHistoryState.paging.loading,
    ...(clearAllFields ? { currentPageNumber: initialBackupHistoryState.paging.currentPageNumber } : {})
  };
  state.totalComputers = initialBackupHistoryState.totalComputers;

  return state;
}
